import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import type { Tab, UserProfile } from '../../../types';

interface TabMembersModalProps {
    isOpen: boolean;
    onClose: () => void;
    tab: Tab;
    memberProfiles: UserProfile[];
    currentUserId: string | undefined;
    isOwner: boolean;
    onRemoveMember: (uid: string) => void;
    onRemoveManualMember: (id: string) => void;
    onAddManualUser: () => void;
    onTogglePaid: (uid: string) => void;
}

export const TabMembersModal: React.FC<TabMembersModalProps> = ({
    isOpen,
    onClose,
    tab,
    memberProfiles,
    currentUserId,
    isOwner,
    onRemoveMember,
    onRemoveManualMember,
    onAddManualUser,
    onTogglePaid
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Members (${tab.members.length + (tab.manualMembers?.length ?? 0)})`}>
            <div className="space-y-3">
                {/* Registered Users */}
                {memberProfiles.map((profile) => {
                    const isPaid = (tab.completedMembers ?? []).includes(profile.uid);
                    return (
                        <div key={profile.uid} className="flex items-center justify-between rounded-xl border border-border-default p-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold relative">
                                    {profile.displayName.slice(0, 2).toUpperCase()}
                                    {isPaid && <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-0.5 border border-white"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>}
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-text-heading flex items-center gap-2">
                                        {profile.displayName} {profile.uid === currentUserId && "(You)"}
                                    </p>
                                    <p className="text-[10px] text-text-muted">
                                        {profile.uid === tab.ownerId ? "Owner" : (isPaid ? "Paid" : "Unpaid")}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {isOwner && profile.uid !== tab.ownerId && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={isPaid ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-50"}
                                            onClick={() => { onTogglePaid(profile.uid); }}
                                        >
                                            {isPaid ? "Paid" : "Mark Paid"}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:bg-red-50"
                                            onClick={() => { onRemoveMember(profile.uid); }}
                                        >
                                            Remove
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Manual Users */}
                {(tab.manualMembers ?? []).map((manualUser) => {
                    const isPaid = (tab.completedMembers ?? []).includes(manualUser.id);
                    return (
                        <div key={manualUser.id} className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/30 p-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 font-bold relative">
                                    {manualUser.name.slice(0, 2).toUpperCase()}
                                    {isPaid && <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-0.5 border border-white"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>}
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-text-heading">{manualUser.name}</p>
                                    <p className="text-[10px] text-amber-600 font-medium">Temporary User • {isPaid ? "Paid" : "Unpaid"}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {isOwner && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={isPaid ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-50"}
                                            onClick={() => { onTogglePaid(manualUser.id); }}
                                        >
                                            {isPaid ? "Paid" : "Mark Paid"}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:bg-red-50"
                                            onClick={() => { onRemoveManualMember(manualUser.id); }}
                                        >
                                            Remove
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isOwner && (
                <div className="mt-4 pt-4 border-t border-border-default">
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={onAddManualUser}
                    >
                        <Plus size={16} className="mr-2" />
                        Add Manual User
                    </Button>
                </div>
            )}
        </Modal>
    );
};
